import { useState, useRef } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Upload, UserPlus, Users, Clock, CheckCircle, AlertCircle, Loader2, Download } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatCard } from "@/components/dashboard/StatCard";
const loadXLSX = () => import("xlsx");

const TEMPLATE_HEADERS = ["full_name", "form_iv_index", "mobile_number", "email", "course", "semester", "class", "nta_level"];
const TEMPLATE_SAMPLE = [
  { full_name: "John Doe", form_iv_index: "S0101/0001/2024", mobile_number: "0712345678", email: "john@email.com", course: "Clinical Medicine", semester: "I", class: "CMT4", nta_level: "5" },
  { full_name: "Jane Smith", form_iv_index: "S0101/0002/2024", mobile_number: "0713456789", email: "", course: "Pharmacy", semester: "II", class: "PST5", nta_level: "6" },
];

const SEMESTER_OPTIONS = ["I", "II"];

const StudentPreRegistration = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    full_name: "", form_iv_index: "", mobile_number: "", email: "", course: "", semester: "", class: "", nta_level: "",
  });

  // Fetch classes from academic courses
  const { data: classOptions = [] } = useQuery({
    queryKey: ["academic-classes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("class")
        .not("class", "is", null);
      if (error) throw error;
      const unique = [...new Set(data.map((c) => c.class).filter(Boolean))] as string[];
      return unique.sort();
    },
  });

  const { data: preRegs = [], isLoading } = useQuery({
    queryKey: ["student-pre-registrations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_pre_registrations")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const stats = {
    total: preRegs.length,
    preRegistered: preRegs.filter((r) => r.onboarding_status === "pre_registered").length,
    otpPending: preRegs.filter((r) => r.onboarding_status === "otp_pending").length,
    active: preRegs.filter((r) => r.onboarding_status === "active").length,
  };

  const addMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const { error } = await supabase.from("student_pre_registrations").insert({
        full_name: data.full_name || null, form_iv_index: data.form_iv_index, mobile_number: data.mobile_number || null,
        email: data.email || null, course: data.course, semester: data.semester, class: data.class,
        program: data.course || null, nta_level: data.nta_level || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-pre-registrations"] });
      toast({ title: "Student pre-registered successfully" });
      setAddOpen(false);
      setForm({ full_name: "", form_iv_index: "", mobile_number: "", email: "", course: "", semester: "", class: "", nta_level: "" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const bulkMutation = useMutation({
    mutationFn: async (rows: typeof form[]) => {
      const uploadedRowsByIndex = new Map<string, typeof form>();

      rows.forEach((row) => {
        const formIvIndex = row.form_iv_index.trim();
        if (!formIvIndex) return;

        const existingRow = uploadedRowsByIndex.get(formIvIndex);
        if (!existingRow) {
          uploadedRowsByIndex.set(formIvIndex, { ...row, form_iv_index: formIvIndex });
          return;
        }

        uploadedRowsByIndex.set(formIvIndex, {
          ...existingRow,
          full_name: existingRow.full_name || row.full_name,
          mobile_number: existingRow.mobile_number || row.mobile_number,
          email: existingRow.email || row.email,
          course: existingRow.course || row.course,
          semester: existingRow.semester || row.semester,
          class: existingRow.class || row.class,
          nta_level: existingRow.nta_level || row.nta_level,
        });
      });

      const uniqueRows = Array.from(uploadedRowsByIndex.values());
      const uploadedIndexes = uniqueRows.map((row) => row.form_iv_index);

      const { data: existing, error: existingError } = await supabase
        .from("student_pre_registrations")
        .select("form_iv_index")
        .in("form_iv_index", uploadedIndexes);

      if (existingError) throw existingError;

      const existingIndexes = new Set((existing || []).map((row) => row.form_iv_index));
      const newRows = uniqueRows.filter((row) => !existingIndexes.has(row.form_iv_index));

      if (newRows.length === 0) {
        return { inserted: 0, skippedExisting: uniqueRows.length, skippedInFile: rows.length - uniqueRows.length };
      }

      const payload = newRows.map((r) => ({
        full_name: r.full_name || null, form_iv_index: r.form_iv_index, mobile_number: r.mobile_number || null,
        email: r.email || null, course: r.course || "", semester: r.semester || "", class: r.class || "",
        program: r.course || null, nta_level: r.nta_level || null,
      }));
      const { error } = await supabase.from("student_pre_registrations").insert(payload);
      if (error) throw error;

      return {
        inserted: newRows.length,
        skippedExisting: uniqueRows.length - newRows.length,
        skippedInFile: rows.length - uniqueRows.length,
      };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["student-pre-registrations"] });
      const skipped = result.skippedExisting + result.skippedInFile;
      toast({ title: `${result.inserted} new students added${skipped > 0 ? `, ${skipped} duplicates skipped` : ""}` });
      setBulkOpen(false);
    },
    onError: (e: any) => toast({ title: "Bulk Upload Error", description: e.message, variant: "destructive" }),
  });

  const parseRows = (rows: any[]) => {
    return rows.filter((r) => r.form_iv_index).map((r) => ({
      full_name: r.full_name ? String(r.full_name).trim() : "",
      form_iv_index: String(r.form_iv_index).trim(),
      mobile_number: r.mobile_number ? String(r.mobile_number).trim() : "",
      email: r.email ? String(r.email).trim() : "",
      course: r.course ? String(r.course).trim() : "",
      semester: r.semester ? String(r.semester).trim() : "",
      class: r.class ? String(r.class).trim() : "",
      nta_level: r.nta_level ? String(r.nta_level).trim() : "",
    }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        let rows: any[] = [];

        if (ext === "xlsx" || ext === "xls") {
          const XLSX = await loadXLSX();
          const data = new Uint8Array(ev.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
          rows = json.map((row: any) => {
            const normalized: any = {};
            Object.keys(row).forEach((k) => { normalized[k.toLowerCase().trim()] = row[k]; });
            return normalized;
          });
        } else {
          const text = ev.target?.result as string;
          const lines = text.split("\n").filter((l) => l.trim());
          if (lines.length < 2) { toast({ title: "File is empty", variant: "destructive" }); return; }
          const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
          rows = lines.slice(1).map((line) => {
            const vals = line.split(",").map((v) => v.trim());
            const obj: any = {};
            headers.forEach((h, i) => (obj[h] = vals[i] || ""));
            return obj;
          });
        }

        if (!rows.length || !rows[0].hasOwnProperty("form_iv_index")) {
          toast({
            title: "Invalid file format",
            description: "File must have column: form_iv_index (and optionally: full_name, mobile_number, email, course, semester, class, nta_level)",
            variant: "destructive",
          });
          return;
        }

        const parsed = parseRows(rows);
        if (parsed.length === 0) { toast({ title: "No valid rows found", variant: "destructive" }); return; }
        bulkMutation.mutate(parsed);
      } catch {
        toast({ title: "Error parsing file", variant: "destructive" });
      }
    };

    if (ext === "xlsx" || ext === "xls") {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDownloadTemplate = async (format: "csv" | "xlsx") => {
    if (format === "xlsx") {
      const XLSX = await loadXLSX();
      const ws = XLSX.utils.json_to_sheet(TEMPLATE_SAMPLE, { header: TEMPLATE_HEADERS });
      ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 22 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Template");
      XLSX.writeFile(wb, "student_pre_registration_template.xlsx");
    } else {
      const csvContent = [TEMPLATE_HEADERS.join(","), ...TEMPLATE_SAMPLE.map((r) => TEMPLATE_HEADERS.map((h) => (r as any)[h]).join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "student_pre_registration_template.csv";
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const filtered = preRegs.filter(
    (r) =>
      `${r.full_name} ${r.form_iv_index} ${r.email || ""} ${r.mobile_number} ${(r as any).course || ""} ${(r as any).class || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );

  const statusColor = (s: string) => {
    switch (s) {
      case "active": return "default";
      case "otp_pending": return "secondary";
      case "signed_up": return "secondary";
      default: return "outline";
    }
  };

  const isFormValid = form.form_iv_index && form.course && form.semester && form.class;

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Student Pre-Registration</h1>
            <p className="text-muted-foreground">Pre-register students before they can sign up</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => handleDownloadTemplate("xlsx")}>
              <Download className="mr-2 h-4 w-4" /> Download Excel
            </Button>
            <Button variant="outline" onClick={() => handleDownloadTemplate("csv")}>
              <Download className="mr-2 h-4 w-4" /> Download CSV
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              <Upload className="mr-2 h-4 w-4" /> Bulk Upload
            </Button>
            <Button onClick={() => setAddOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" /> Add Student
            </Button>
          </div>
        </div>

        <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Pre-Registered" value={stats.total} icon={Users} description="All students" />
          <StatCard title="Awaiting Signup" value={stats.preRegistered} icon={Clock} description="Not yet signed up" />
          <StatCard title="OTP Pending" value={stats.otpPending} icon={AlertCircle} description="Verification in progress" />
          <StatCard title="Active Students" value={stats.active} icon={CheckCircle} description="Fully onboarded" />
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name, index, email, course, or class..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Pre-Registered Students</CardTitle>
            <CardDescription>{filtered.length} records</CardDescription>
          </CardHeader>
          <CardContent className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Form IV Index</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Semester</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.full_name}</TableCell>
                    <TableCell>{r.form_iv_index}</TableCell>
                    <TableCell>{r.mobile_number}</TableCell>
                    <TableCell>{(r as any).course || "—"}</TableCell>
                    <TableCell>{(r as any).semester || "—"}</TableCell>
                    <TableCell>{(r as any).class || "—"}</TableCell>
                    <TableCell>
                      <Badge variant={statusColor(r.onboarding_status) as any}>
                        {r.onboarding_status.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      {isLoading ? "Loading..." : "No pre-registrations found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add Student Dialog */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Pre-Register Student</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Form IV Index *</Label>
                  <Input value={form.form_iv_index} onChange={(e) => setForm({ ...form, form_iv_index: e.target.value })} placeholder="S0101/0001/2024" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mobile Number *</Label>
                  <Input value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} placeholder="0713664912" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Course *</Label>
                  <Input value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} placeholder="e.g., Clinical Medicine" />
                </div>
                <div className="space-y-2">
                  <Label>Semester *</Label>
                  <Select value={form.semester} onValueChange={(v) => setForm({ ...form, semester: v })}>
                    <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                    <SelectContent>
                      {SEMESTER_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>Semester {s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Class *</Label>
                  <Select value={form.class} onValueChange={(v) => setForm({ ...form, class: v })}>
                    <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>
                      {classOptions.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>NTA Level</Label>
                  <Select value={form.nta_level} onValueChange={(v) => setForm({ ...form, nta_level: v })}>
                    <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                    <SelectContent>
                      {["4", "5", "6", "7", "8"].map((l) => (
                        <SelectItem key={l} value={l}>NTA Level {l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                className="w-full"
                disabled={!isFormValid || addMutation.isPending}
                onClick={() => addMutation.mutate(form)}
              >
                {addMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                Pre-Register Student
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Bulk Upload Students</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg border-2 border-dashed p-6 text-center space-y-3">
                <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Upload a CSV or Excel file with student data</p>
                <p className="text-xs text-muted-foreground">
                  Required column: <strong>form_iv_index</strong><br />
                  Recommended: course, semester, class<br />
                  Optional: full_name, mobile_number, email, nta_level
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={bulkMutation.isPending}>
                  {bulkMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  Select CSV or Excel File
                </Button>
              </div>
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-xs font-medium mb-1">Required column:</p>
                <pre className="text-xs text-muted-foreground">
{`form_iv_index
Recommended: course, semester, class
Optional: full_name, mobile_number, email, nta_level`}
                </pre>
                <p className="text-xs text-muted-foreground mt-2">
                  Use the <strong>Download Excel</strong> or <strong>Download CSV</strong> button to get a ready-made template.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default StudentPreRegistration;
